import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '@/components/SEO';
import AnalyticsWithTransition from '@/components/AnalyticsWithTransition';

const About: React.FC = () => {
  const navigate = useNavigate();

  return (
    <>
      <SEO
        title="About DomBase - Our Mission & Story"
        description="Learn about DomBase, our mission to make domain research accessible to everyone, and our commitment to providing free, accurate domain availability checking and WHOIS lookup tools."
        canonical="/about"
        ogTitle="About DomBase - Our Mission & Story"
        ogDescription="Learn about DomBase, our mission to make domain research accessible to everyone, and our commitment to providing free domain tools."
        ogUrl="/about"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'About', url: '/about' }
        ]}
      />
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="mb-8">
            <Link to="/" className="text-blue-600 dark:text-blue-400 hover:underline mb-4 inline-block">
              ← Back to Home
            </Link>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              About DomBase
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Making domain research accessible to everyone
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 space-y-8">

            <section>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Our Mission</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
                At DomBase, we believe that finding the perfect domain name should be simple, fast, and accessible to everyone. Our mission is to empower entrepreneurs, startups, and businesses of all sizes with the tools and information they need to make confident domain decisions.
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                We're committed to providing free, accurate, and comprehensive domain research tools that help you find available domains, understand domain data, and make informed choices about your online presence. Whether you're launching your first website or expanding an established brand, DomBase is here to support your journey.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Our Story</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
                DomBase was born from a simple frustration: finding accurate domain availability information was unnecessarily complicated and often required multiple tools and services. As entrepreneurs ourselves, we experienced firsthand how difficult it could be to quickly check domain availability, research WHOIS data, and evaluate domain options.
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
                We set out to create a platform that combines all the essential domain research tools in one place, with a focus on accuracy, speed, and user experience. We built DomBase to be the tool we wished we had when starting our own projects—a comprehensive, free, and easy-to-use domain research platform.
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                Today, DomBase serves thousands of users who rely on our platform for domain availability checking, WHOIS lookups, and domain research. We continue to improve our tools and add new features based on user feedback, always staying true to our mission of making domain research accessible to everyone.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">What We Offer</h2>

              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-3">Free Domain Availability Checking</h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    Check domain availability across hundreds of TLDs with 99.9%+ accuracy. Our real-time checking system queries authoritative sources directly, ensuring you get the most current availability status for any domain name.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-3">Comprehensive WHOIS Data</h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    Access detailed WHOIS information including registration details, expiration dates, registrar information, and nameserver data. Our WHOIS lookup tool provides comprehensive domain information to support your research and decision-making.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-3">Bulk Domain Checking</h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    Check hundreds or thousands of domains simultaneously with our bulk checking feature. Upload CSV files or paste domain lists for efficient batch processing with real-time progress tracking.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-3">Smart Valuation</h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    Get instant domain valuations scored on length, TLD tier, keyword strength, memorability, and age. No machine learning — just a transparent weighted formula that helps you compare domains quickly.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-3">Brand Risk Analysis</h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    Evaluate potential brand risks associated with domain names, helping you avoid domains that might cause confusion or legal issues. Our brand risk analysis considers similarity to existing brands and potential trademark conflicts.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Our Values</h2>

              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Accessibility</h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    We believe domain research tools should be free and accessible to everyone, regardless of budget or technical expertise. DomBase will always offer core features free of charge, ensuring that anyone can find and research domain names.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Accuracy</h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    We're committed to providing the most accurate domain information possible. Our systems query authoritative sources directly and are continuously updated to ensure 99.9%+ accuracy in availability checking and WHOIS data.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Privacy</h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    We respect your privacy and don't store your search history or personal information. All domain checks are processed in real-time without logging, ensuring your domain research remains private and secure.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">User Experience</h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    We prioritize user experience in everything we build. Our interface is designed to be intuitive, fast, and accessible, whether you're a first-time domain searcher or an experienced domain investor.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Our Commitment</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
                We're committed to continuously improving DomBase based on user feedback and evolving needs. We regularly add new features, improve accuracy, and enhance user experience to ensure DomBase remains the best free domain research tool available.
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                As we grow, we'll maintain our core values of accessibility, accuracy, and privacy. DomBase will always offer free domain availability checking and WHOIS lookups, and we'll continue to innovate to provide even more value to our users.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Get in Touch</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
                We'd love to hear from you! Whether you have feedback, suggestions, questions, or just want to say hello, we're here to listen. Your input helps us improve DomBase and better serve the community.
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                If you encounter any issues, have feature requests, or want to share your experience using DomBase, please don't hesitate to reach out. We're committed to providing excellent support and continuously improving our platform based on user needs.
              </p>
            </section>

            <div className="mt-12 p-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Ready to Get Started?</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Start using DomBase today to find your perfect domain name. Our free tools are available to everyone, no registration required.
              </p>
              <button
                onClick={() => navigate('/', { state: { focusSearch: true } })}
                className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Start Your Domain Search
              </button>
            </div>
          </div>

          {/* Ads Section - Only show after substantial content */}
          <div className="mt-8 flex justify-center">
            <AnalyticsWithTransition
              variant="hero"
              show={true}
              className="transition-all duration-700 ease-in-out"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default About;

